import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { customerApi } from '../lib/api'
import {
  db,
  debtByCustomer,
  debtOrdersOf,
  payDebt,
  type Customer,
  type PayMethod,
} from '../lib/db'
import { bare } from '../lib/menuImages'
import { clock, dayMonth, money } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { pushNow } from '../lib/sync'
import { AppBar } from '../components/AppBar'
import { NumPad } from '../components/NumPad'
import { Sheet } from '../components/Sheet'

/**
 * Sổ nợ khách quen.
 *
 * Mỗi quán có vài người được mang đồ về trước rồi trả sau: hàng xóm, mối sỉ, mấy anh công
 * trình bên cạnh. Trước đây nhân viên ghi vào tờ giấy dán ở quầy — tờ giấy rách hoặc thất
 * lạc là không ai chứng minh được gì, và chuyện đó làm mất cả tiền lẫn bạn.
 *
 * Số dư không có chỗ lưu: nó là tổng đơn ghi sổ trừ tổng tiền đã trả, tính lại mỗi lần mở
 * màn hình. Lưu sẵn thì phải nhớ cập nhật ở năm chỗ, quên một chỗ là con số ấy nói dối mãi.
 */

export function CustomersScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const owner = isOwner(useApp((s) => s.session?.role))

  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<Customer | 'new' | null>(null)
  const [picked, setPicked] = useState<Customer | null>(null)

  const customers = useLiveQuery(() => db.customers.orderBy('name').toArray(), [], [])
  const debts = useLiveQuery(() => debtByCustomer(), [], new Map<string, number>())

  const shown = useMemo(() => {
    const q = bare(query)
    return customers
      .filter((c) => !q || bare(c.name).includes(q) || (c.phone ?? '').includes(q))
      .sort((a, b) => (debts.get(b.id) ?? 0) - (debts.get(a.id) ?? 0) || a.name.localeCompare(b.name))
  }, [customers, debts, query])

  const total = useMemo(
    () => shown.reduce((s, c) => s + Math.max(0, debts.get(c.id) ?? 0), 0),
    [shown, debts],
  )
  const owing = shown.filter((c) => (debts.get(c.id) ?? 0) > 0).length

  return (
    <div className="screen">
      <AppBar
        title="Khách quen"
        sub={owing > 0 ? `${owing} người đang nợ · ${money(total)}đ` : 'Chưa ai nợ đồng nào'}
        onBack={() => nav('/settings')}
        right={
          owner ? (
            <button type="button" className="btn btn-brand btn-sm" onClick={() => setEditing('new')}>
              + Thêm
            </button>
          ) : undefined
        }
      />

      <div className="body">
        {customers.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              📒
            </div>
            <p>
              Chưa có khách quen nào. Thêm một người rồi ở màn hình thu tiền sẽ có thêm cách
              "Ghi sổ".
            </p>
          </div>
        ) : (
          <>
            <label className="field">
              <span className="sr-only">Tìm khách</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tìm tên hoặc số điện thoại…"
              />
            </label>

            <div className="list">
              {shown.map((c) => {
                const debt = debts.get(c.id) ?? 0
                const over = c.debtLimit > 0 && debt >= c.debtLimit

                return (
                  <button
                    key={c.id}
                    type="button"
                    className="row-btn"
                    onClick={() => setPicked(c)}
                  >
                    <span className="row-text">
                      <b>{c.name}</b>
                      <small>
                        {c.phone ?? 'Chưa có số điện thoại'}
                        {c.debtLimit > 0 ? ` · trần ${money(c.debtLimit)}đ` : ''}
                      </small>
                    </span>
                    <span className={`row-price num${debt > 0 ? ' is-owing' : ''}`}>
                      {debt > 0 ? `${money(debt)}đ` : '—'}
                    </span>
                    {over && <span className="tag-over">Vượt trần</span>}
                    <span aria-hidden="true">›</span>
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>

      {editing !== null && (
        <CustomerEditor
          customer={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await refreshCatalog()
          }}
          onDeleted={async () => {
            setEditing(null)
            setPicked(null)
            await refreshCatalog()
          }}
          say={say}
        />
      )}

      {picked && (
        <CustomerDetail
          customer={picked}
          owner={owner}
          onClose={() => setPicked(null)}
          onEdit={() => {
            setEditing(picked)
            setPicked(null)
          }}
          say={say}
        />
      )}
    </div>
  )
}

/** Thêm hoặc sửa một khách. Cần mạng, vì danh sách này nằm trên máy chủ. */
function CustomerEditor({
  customer,
  onClose,
  onSaved,
  onDeleted,
  say,
}: {
  customer: Customer | null
  onClose: () => void
  onSaved: () => void | Promise<void>
  onDeleted: () => void | Promise<void>
  say: (m: string) => void
}) {
  const [name, setName] = useState(customer?.name ?? '')
  const [phone, setPhone] = useState(customer?.phone ?? '')
  const [note, setNote] = useState(customer?.note ?? '')
  const [limit, setLimit] = useState(customer?.debtLimit ?? 0)
  const [busy, setBusy] = useState(false)

  async function save() {
    if (!name.trim()) {
      say('Chưa có tên khách')
      return
    }

    setBusy(true)
    try {
      await customerApi.save({
        id: customer?.id,
        name: name.trim(),
        phone: phone.trim() || null,
        note: note.trim() || null,
        debtLimit: limit,
      })
      await onSaved()
    } catch (e) {
      say(e instanceof Error ? e.message : 'Cần có mạng để lưu khách quen')
    }
    setBusy(false)
  }

  async function remove() {
    if (!customer) return
    setBusy(true)
    try {
      await customerApi.remove(customer.id)
      await onDeleted()
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa xoá được')
    }
    setBusy(false)
  }

  return (
    <Sheet title={customer ? customer.name : 'Thêm khách quen'} onClose={onClose}>
      <label className="field">
        <span>Tên khách</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />
      </label>

      <label className="field">
        <span>Số điện thoại</span>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
          inputMode="numeric"
          placeholder="Để gọi khi tới kỳ thu"
        />
      </label>

      {/*
        Trần nợ không chặn, chỉ báo.
        Chặn cứng thì nhân viên gặp khách đứng trước mặt mà máy không cho ghi, và họ sẽ ghi
        ra giấy — lúc ấy quán mất luôn cả sổ. Báo đỏ để họ biết mà hỏi chủ quán thì hơn.
      */}
      <div className="field">
        <span>Trần nợ</span>
        <div className="amount-readout num">{limit > 0 ? money(limit) : 'Không đặt trần'}</div>
        <div className="chip-row">
          {[0, 500_000, 1_000_000, 2_000_000, 5_000_000].map((v) => (
            <button
              key={v}
              type="button"
              className="chip"
              aria-pressed={limit === v}
              onClick={() => setLimit(v)}
            >
              {v === 0 ? 'Không đặt' : money(v)}
            </button>
          ))}
        </div>
        <small className="note wide">
          Vượt trần thì máy vẫn cho ghi sổ, chỉ báo đỏ để nhân viên biết mà hỏi chủ quán.
        </small>
      </div>

      <label className="field">
        <span>Ghi chú</span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
          placeholder="Mối sỉ, hàng xóm, công ty bên cạnh…"
        />
      </label>

      <div className="sheet-actions">
        {customer && (
          <button type="button" className="btn btn-danger-ghost" onClick={() => void remove()} disabled={busy}>
            Xoá
          </button>
        )}
        <button type="button" className="btn btn-brand grow" onClick={() => void save()} disabled={busy}>
          {busy ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </Sheet>
  )
}

/** Sổ của một người: còn nợ bao nhiêu, nợ từ đơn nào, đã trả mấy lần. */
function CustomerDetail({
  customer,
  owner,
  onClose,
  onEdit,
  say,
}: {
  customer: Customer
  owner: boolean
  onClose: () => void
  onEdit: () => void
  say: (m: string) => void
}) {
  const [paying, setPaying] = useState(false)

  const orders = useLiveQuery(() => debtOrdersOf(customer.id), [customer.id], [])
  const payments = useLiveQuery(
    () => db.debtPayments.where('customerId').equals(customer.id).reverse().sortBy('paidAt'),
    [customer.id],
    [],
  )

  const owed = orders.reduce((s, o) => s + o.total, 0) - payments.reduce((s, p) => s + p.amount, 0)

  if (paying) {
    return (
      <DebtPaySheet
        customer={customer}
        owed={owed}
        onClose={() => setPaying(false)}
        onDone={() => {
          setPaying(false)
          say(`Đã ghi nhận tiền của ${customer.name}`)
        }}
      />
    )
  }

  return (
    <Sheet
      title={customer.name}
      onClose={onClose}
      right={
        owner ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
            Sửa
          </button>
        ) : undefined
      }
    >
      <div className={`debt-sum${owed > 0 ? ' is-owing' : ''}`}>
        <span>Còn nợ</span>
        <b className="num">{money(Math.max(0, owed))}đ</b>
        {owed < 0 && <small>Khách đã trả dư {money(-owed)}đ</small>}
      </div>

      {owed > 0 && (
        <button type="button" className="btn btn-brand wide" onClick={() => setPaying(true)}>
          Khách trả tiền
        </button>
      )}

      <h2 className="section-label">Đơn ghi sổ ({orders.length})</h2>
      {orders.length === 0 ? (
        <p className="note wide">Chưa có đơn nào ghi sổ cho người này.</p>
      ) : (
        <div className="list">
          {orders.map((o) => (
            <div key={o.id} className="list-row">
              <span className="list-name">
                {o.code}
                <small> · {o.paidAt ? `${clock(o.paidAt)} ${dayMonth(o.paidAt)}` : ''}</small>
              </span>
              <span className="list-amount num">{money(o.total)}đ</span>
            </div>
          ))}
        </div>
      )}

      <h2 className="section-label">Đã trả ({payments.length})</h2>
      {payments.length === 0 ? (
        <p className="note wide">Chưa trả lần nào.</p>
      ) : (
        <div className="list">
          {payments.map((p) => (
            <div key={p.id} className="list-row">
              <span className="list-name">
                {p.method === 'Transfer' ? 'Chuyển khoản' : 'Tiền mặt'}
                <small> · {clock(p.paidAt)} {dayMonth(p.paidAt)}</small>
              </span>
              <span className="list-amount num is-paid">{money(p.amount)}đ</span>
            </div>
          ))}
        </div>
      )}

      {customer.note && <p className="note wide">{customer.note}</p>}
    </Sheet>
  )
}

/**
 * Khách đưa tiền trả nợ.
 *
 * Cho trả từng phần: người ta đưa được bao nhiêu thì ghi bấy nhiêu, và đó là cách tiền
 * thật đi vào quán. Bắt trả đủ mới cho bấm thì nhân viên sẽ ghi ra giấy.
 */
function DebtPaySheet({
  customer,
  owed,
  onClose,
  onDone,
}: {
  customer: Customer
  owed: number
  onClose: () => void
  onDone: () => void
}) {
  const [amount, setAmount] = useState(owed)
  const [method, setMethod] = useState<PayMethod>('Cash')
  const [busy, setBusy] = useState(false)

  const ok = amount > 0 && amount <= owed

  async function save() {
    setBusy(true)
    await payDebt(customer.id, amount, method)
    void pushNow()
    setBusy(false)
    onDone()
  }

  return (
    <Sheet title={`${customer.name} trả tiền`} onClose={onClose}>
      <p className="sheet-note">
        Đang nợ {money(owed)}đ. Trả bao nhiêu ghi bấy nhiêu, không cần trả hết một lần.
      </p>

      <div className="field">
        <span>Khách đưa</span>
        <div className="amount-readout num">{money(amount)}</div>
        <div className="chip-row">
          <button
            type="button"
            className="chip is-exact"
            aria-pressed={amount === owed}
            onClick={() => setAmount(owed)}
          >
            Trả hết
          </button>
          {[100_000, 200_000, 500_000]
            .filter((v) => v < owed)
            .map((v) => (
              <button
                key={v}
                type="button"
                className="chip"
                aria-pressed={amount === v}
                onClick={() => setAmount(v)}
              >
                {money(v)}
              </button>
            ))}
        </div>
      </div>

      <div className="seg">
        <button type="button" aria-pressed={method === 'Cash'} onClick={() => setMethod('Cash')}>
          💵 Tiền mặt
        </button>
        <button
          type="button"
          aria-pressed={method === 'Transfer'}
          onClick={() => setMethod('Transfer')}
        >
          📱 Chuyển khoản
        </button>
      </div>

      <NumPad value={amount} onChange={setAmount} max={owed} />

      <div className="sheet-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Quay lại
        </button>
        <button
          type="button"
          className="btn btn-brand grow"
          disabled={!ok || busy}
          onClick={() => void save()}
        >
          {busy ? 'Đang ghi…' : `Nhận ${money(amount)}đ`}
        </button>
      </div>
    </Sheet>
  )
}
